// client/src/pages/secure/ClientListPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Import Link
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import PageHeader from '../../components/shared/PageHeader';
import { getCurrentUser } from '../../hooks/useCurrentUser';

// Same rule as the server (lib/agentAccess.mayFileIntake): office staff file pasted inquiries,
// because a filed e-mail is matched against every client in the firm.
const mayPasteInquiry = (u) => !!u && u.role !== 'examiner'
    && !!(u.isManagingBroker || u.role === 'admin' || ['Office Manager', 'Staff', 'Read Only'].includes(u.staffRole));

const AddClientModal = ({ isOpen, onClose, onClientAdded }) => {
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', clientType: 'Buyer' });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            const newClient = await apiClient.post('/api/clients', formData);
            onClientAdded(newClient);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create client.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Add New Client</h2>
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className="p-2 border rounded" />
                        <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className="p-2 border rounded" />
                        <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email Address" className="p-2 border rounded" />
                        <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" className="p-2 border rounded" />
                        <select name="clientType" value={formData.clientType} onChange={handleChange} className="p-2 border rounded md:col-span-2">
                            <option value="Buyer">Buyer</option>
                            <option value="Seller">Seller</option>
                            <option value="Tenant">Tenant</option>
                            <option value="Landlord">Landlord</option>
                        </select>
                    </div>
                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Client'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Paste an inquiry e-mail (headers optional): it becomes a lead, matched to an existing
// client by e-mail or phone (POST /api/desk/intake/email/paste, lib/emailIntake).
const PasteEmailModal = ({ isOpen, onClose, onFiled }) => {
    const apiClient = useApiClient();
    const [text, setText] = useState('');
    const [msg, setMsg] = useState('');
    if (!isOpen) return null;
    const file = async () => {
        try {
            const r = await apiClient.post('/api/desk/intake/email/paste', { text });
            setMsg(r.created ? `New lead #${r.client_id} created.` : `Added to existing client #${r.client_id}.`);
            setText(''); onFiled(r);
        } catch (e) { setMsg(e.message); }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-2xl space-y-3">
                <h2 className="text-xl font-bold text-slate-800">Paste an e-mail inquiry</h2>
                <p className="text-sm text-slate-600">Paste the whole e-mail, including the From: and Subject: lines if you have them. The sender becomes a lead (or is matched to an existing client) and the message is kept on the timeline. Nothing is sent to them.</p>
                <textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} className="w-full p-2 border rounded font-mono text-sm" placeholder={'From: Jane Doe <jane@example.com>\nSubject: 2-flat on Kedzie\n\nHi, is it still available? Call me at 312-555-0100.'} />
                {msg && <p className="text-sm text-indigo-800">{msg}</p>}
                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Close</button>
                    <button onClick={file} disabled={text.trim().length < 10} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-300">File as a lead</button>
                </div>
            </div>
        </div>
    );
};

const PIPELINE_STAGES = ['Lead', 'Active Prospect', 'Under Contract', 'Closed Client', 'Past Client', 'Lost'];

const ClientListPage = () => {
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pasteOpen, setPasteOpen] = useState(false);
    const [stageFilter, setStageFilter] = useState('All');
    const [sourceFilter, setSourceFilter] = useState('all');
    const apiClient = useApiClient();

    useEffect(() => {
        apiClient.get('/api/clients')
            .then(data => setClients(data))
            .catch(err => setError(err.message || 'Failed to fetch clients.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    const handleClientAdded = (newClient) => {
        setClients(prevClients => [newClient, ...prevClients]);
    };

    const visibleClients = clients
        .filter(c => stageFilter === 'All' || c.pipeline_stage === stageFilter)
        .filter(c => sourceFilter === 'all' || (sourceFilter === 'owners' ? !!Number(c.is_website_owner) : !Number(c.is_website_owner)));

    return (
        <>
            <PasteEmailModal isOpen={pasteOpen} onClose={() => setPasteOpen(false)}
                onFiled={() => apiClient.get('/api/clients').then(setClients).catch(() => {})} />
            <AddClientModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onClientAdded={handleClientAdded}
            />
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow">
                <PageHeader title="Client Management" description="Buyers, sellers, tenants and landlords, with their stage and where they came from."
                    actions={
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                        >
                            Add New Client
                        </button>
                    }
                    secondary={<>
                        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="p-2 border rounded text-sm">
                            <option value="All">All Stages</option>
                            {PIPELINE_STAGES.map(stage => <option key={stage} value={stage}>{stage}</option>)}
                        </select>
                        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="p-2 border rounded text-sm" aria-label="Client source">
                            <option value="all">All clients</option>
                            <option value="agents">Working with agents</option>
                            <option value="owners">Website owners (self-listed)</option>
                        </select>
                        {mayPasteInquiry(getCurrentUser()) && <button onClick={() => setPasteOpen(true)} className="px-4 py-2 text-sm font-medium bg-slate-100 border border-slate-300 rounded-md hover:bg-slate-200">Paste an e-mail</button>}
                    </>} />

                {isLoading && <p>Loading clients...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <DataList rows={visibleClients} rowKey="client_id" csvName="clients" defaultSort={{ key: 'client_id', dir: 'desc' }}
                        filters={[{ key: 'client_type', label: 'Type' }, { key: 'lead_source', label: 'Source' }]}
                        columns={[
                            { key: 'client_id', label: '#', type: 'number' },
                            { key: 'name', label: 'Name', value: (c) => `${c.first_name} ${c.last_name}`, render: (client) => (<>
                                <Link to={`/dashboard/clients/${client.client_id}`} className="text-indigo-700 font-medium">{client.first_name} {client.last_name}</Link>
                                {!!Number(client.is_website_owner) && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" title="Signed up on the website to list their own property">Website owner</span>}
                            </>) },
                            { key: 'email', label: 'Email' },
                            { key: 'phone_number', label: 'Phone' },
                            { key: 'client_type', label: 'Type' },
                            { key: 'pipeline_stage', label: 'Stage' },
                            { key: 'lead_source', label: 'Source' },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (client) => <Link to={`/dashboard/clients/${client.client_id}`} className="text-indigo-600 hover:text-indigo-900">Manage</Link> },
                        ]} />
                )}
            </div>
        </>
    );
};

export default ClientListPage;
